import { useRef } from 'react';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent } from '@/components/ui/card';

interface CalculatorSliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  displayValue: string;
  onChange: (value: number) => void;
  minLabel?: string;
  maxLabel?: string;
  /** Label shown below the default-value marker */
  defaultLabel?: string;
}

export default function CalculatorSlider({
  label,
  value,
  min,
  max,
  step,
  displayValue,
  onChange,
  minLabel,
  maxLabel,
  defaultLabel,
}: CalculatorSliderProps) {
  const initialValue = useRef(value);

  const markerPct = ((initialValue.current - min) / (max - min)) * 100;
  // Hide marker when too close to edges (within 12%) to prevent overlap
  const showMarker = markerPct > 12 && markerPct < 88;
  const markerLabel = defaultLabel ?? (initialValue.current % 1 === 0 ? String(initialValue.current) : initialValue.current.toFixed(1));

  return (
    <Card size="sm">
      <CardContent className="flex flex-col gap-1">
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-[13px] text-muted-foreground">{label}</span>
          <span className="text-[15px] font-semibold tabular-nums text-foreground">{displayValue}</span>
        </div>
        {/* Slider with marker line overlay */}
        <div className="relative">
          <Slider
            min={min}
            max={max}
            step={step}
            value={[value]}
            onValueChange={(val) => onChange(Array.isArray(val) ? val[0] : val)}
          />
          {/* Vertical marker line on the track */}
          {showMarker && (
            <div
              className="absolute top-0 h-full w-px bg-foreground/30 pointer-events-none -z-10"
              style={{ left: `calc(8px + ${markerPct}% * (100% - 16px) / 100%)` }}
            />
          )}
        </div>
        {/* Labels row: min — marker — max */}
        <div className="relative h-4 text-[11px] text-muted-foreground">
          <span className="absolute left-0">{minLabel || min}</span>
          {showMarker && (
            <span
              className="absolute -translate-x-1/2"
              style={{ left: `${markerPct}%` }}
              title="Average value for this period"
            >
              {markerLabel}
            </span>
          )}
          <span className="absolute right-0">{maxLabel || max}</span>
        </div>
      </CardContent>
    </Card>
  );
}
